/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, withAssign, withInit } from '@reatom/framework'

import { AsyncStateN } from '@blade/std-lib'
import { isAtomMut } from '@root/shared/lib'

import type { Atom, AtomMut, Ctx, Fn } from '@reatom/framework'

export type TableData<D extends object> = {
  data: Array<D>
  cursor?: string | undefined
  hasNext?: boolean
  total?: number
  // TODO: Should be synced with metadata from BE
}

export type DataTableStateArgs<D extends object> = {
  name: string
  stateAtom?: Atom<AsyncStateN.AsyncStateN<unknown, TableData<D>>>
  initialItems?: Array<D> | ((ctx: Ctx) => Array<D>)
  getId: (item: D) => string | number
  /**
   * The most rows to keep when they arrive through `upsertItems`.
   *
   * A table fed from the socket has no end: every trade that passes its
   * filter was pushed onto the array and kept, and its id kept beside it in
   * a set that was never pruned. Measured on the explore screen on
   * 2026-09-23: the heap grew 36-49 MB a minute with the DOM unchanged at
   * 1,217 nodes, and the renderer stopped answering after about four
   * minutes — long enough that a screenshot could not be taken.
   *
   * The copy on every batch is the other half of it. `upsertItems` rebuilds
   * the whole array, and the socket delivers a batch per animation frame, so
   * the work per second grows with the length of the list. Bounding the list
   * bounds that too.
   *
   * Left unset the table keeps everything, which is what a table paging
   * through history wants. Set it on anything the socket writes to.
   */
  maxItems?: number
}

export const reatomDataTableState = <D extends object>({
  name,
  stateAtom,
  initialItems,
  getId,
  maxItems,
}: DataTableStateArgs<D>) => {
  type State = AsyncStateN.AsyncStateN<unknown, TableData<D>>
  const idsSet = new Set<string | number>()

  const controlledStateAtom = stateAtom
    ? // Wrap in computed to avoid accidental mutations
      atom((ctx) => ctx.spy(stateAtom))
    : undefined

  const itemsStateAtom: AtomMut<State> | Atom<State> =
    controlledStateAtom ??
    atom(
      AsyncStateN.loading<unknown, TableData<D>>(),
      `${name}.itemsStateAtom`,
    ).pipe(
      withInit((ctx) => {
        if (!initialItems) {
          return AsyncStateN.loading<unknown, TableData<D>>()
        }

        const items = (() => {
          if (typeof initialItems === 'function') {
            return initialItems(ctx)
          }
          return initialItems
        })()

        for (const item of items) {
          idsSet.add(getId(item))
        }

        return AsyncStateN.success({ data: items })
      }),
    )

  type Update<State> = State | Fn<[State, Ctx], State>
  const setStateAction = action(
    (ctx, state: Update<AsyncStateN.AsyncStateN<unknown, TableData<D>>>) => {
      if (isAtomMut(itemsStateAtom)) {
        const currentState = ctx.get(itemsStateAtom)
        const state_ = (() => {
          if (typeof state !== 'function') {
            return state
          }
          return state(currentState, ctx)
        })()

        if (AsyncStateN.isSuccess(state_)) {
          const newItems = state_.value.data
          for (const item of newItems) {
            idsSet.add(getId(item))
          }
        } else {
          idsSet.clear()
        }

        itemsStateAtom(ctx, state_)
      }
    },
    `${name}.setStateAction`,
  )

  const upsertItemsAction = action(
    async (
      ctx,
      items: Array<D>,
      options: {
        insertMode: 'prepend' | 'append'
      },
    ) => {
      if (!isAtomMut(itemsStateAtom)) {
        return
      }
      const state = ctx.get(itemsStateAtom)
      if (!AsyncStateN.isSuccess(state)) {
        return
      }
      const currentItems = state.value.data
      const updatedItems = [...currentItems]

      for (const item of items) {
        if (idsSet.has(getId(item))) {
          continue
        }

        idsSet.add(getId(item))
        if (options.insertMode === 'append') {
          updatedItems.push(item)
        } else {
          updatedItems.unshift(item)
        }
      }

      // The rows that fall off the far end go, and their ids go with them —
      // a set that outlives its rows leaks just as surely as the rows would,
      // and it would also refuse a trade that scrolled away and came back.
      if (maxItems !== undefined && updatedItems.length > maxItems) {
        const dropped =
          options.insertMode === 'append'
            ? updatedItems.splice(0, updatedItems.length - maxItems)
            : updatedItems.splice(maxItems)
        for (const item of dropped) {
          idsSet.delete(getId(item))
        }
      }

      const newData: TableData<D> = {
        ...state.value,
        data: updatedItems,
      }
      itemsStateAtom(ctx, AsyncStateN.success(newData))
    },
  )

  return itemsStateAtom.pipe(
    withAssign(() => ({
      setState: setStateAction,
      upsertItems: upsertItemsAction,
      idsSet,
    })),
  )
}

export interface DataTableStateModel<D extends object>
  extends ReturnType<typeof reatomDataTableState<D>> {}

export type DataTableItemsState<D extends object> = AtomMut<
  AsyncStateN.AsyncStateN<unknown, { data: Array<D> }>
>
